import { DocumentEvents } from "./../helpers/document-events";
import Marionette from "../../shared/marionette-mustache";
import {DesktopModel} from "../../desktop/models/desktop";
import {autoinject, Container} from "aurelia-dependency-injection";
import {SizeManager} from "../../shared/helpers/size-manager";
import {CaseDocumentModel} from "../../desktop/models/case-document";
import {DocumentHolderView} from "../../desktop/views/document-holder";
import {DocumentHolderModel} from "../../desktop/models/document-holder";
import {DocumentHolderCollection} from "../../desktop/collections/document-holders";
import {TemplateManager} from "../../shared/helpers/template-manager";
import {MasterView} from "../../shared/views/master-view";
import {backboneViewProperties} from "../../decorators/backbone-properties";
import {AppEvents} from "../../app-events";
import {DeviceHelper} from "../../shared/helpers/device-helper";
import { MobileHelper } from "../../shared/helpers/mobile-helper";

const template = require<string>("../../desktop/views/desktop.html");

export enum TRANSITION {
    ANIMATED,
    INSTANT
}

const dom = {
    DESKTOP_CONTAINER: "#desktop-container",
    DOCUMENT_HOLDER_TEMPLATE: "#document-holder-template",
    LEFT_BUTTON: "#desktop-left",
    RIGHT_BUTTON: "#desktop-right",
    DESKTOP_MODE_BUTTON: "#desktop-mode-button",
    // DESKTOP_MODE_BUTTONS: "#desktop-mode-buttons",
    DESKTOP_ARROW_BUTTONS: "#desktop-arrow-buttons",
    DESKTOP_STACK_BUTTON: "#desktop-stack-button",
    MULTIVIEW_BUTTON: "#btn-desktop-mode-multiview",
    SINGLEVIEW_BUTTON: "#btn-desktop-mode-single",
    OPEN_DOCUMENTS_BUTTON: "#open-documents-button",
    OPEN_DOCUMENT_LINKS: ".open-document-link",
    RESULTS_RETURN_BUTTON: "#results-return-button .btn",
    CLOSER: ".document-closer",
    CATEGORY_TREND: ".category-trend",
    MOBILE_MASK: ".mobile-mask",
};

@backboneViewProperties({
    template
})
@autoinject
export class DesktopView extends Marionette.ItemView<any> {

    public openCiteLineCount = 0;

    constructor(
        public model: DesktopModel,
        private appEvents: Backbone.Events,
        private sizeManager: SizeManager,
        private masterView: MasterView,
        private templateManager: TemplateManager) {
        super();
        this.setupModelChangeListeners();
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events[`click ${dom.LEFT_BUTTON}`] = this.leftDesktopClick;
        events[`click ${dom.RIGHT_BUTTON}`] = this.rightDesktopClick;
        events[`click ${dom.DESKTOP_MODE_BUTTON}`] = this.desktopModeButtonClick;
        events[`click ${dom.DESKTOP_STACK_BUTTON}`] = this.desktopStackButtonClick;
        events[`click ${dom.OPEN_DOCUMENTS_BUTTON}`] = this.openDocumentsButtonClick;
        events[`click ${dom.OPEN_DOCUMENT_LINKS}`] = this.openDocumentLinkClick;
        events[`click ${dom.RESULTS_RETURN_BUTTON}`] = this.resultsReturnClick;
        events[`click ${dom.CLOSER}`] = this.closeDocument;
        const mh: MobileHelper = Container.instance.get(MobileHelper);
        if (mh.IsMobileMode) {
            events[`click ${dom.MOBILE_MASK}`] = this.mobileMaskClick;
        }
        return events;
    }

    public mobileMaskClick() {
        this.masterView.mobileHideDocumentList();
    }

    public setupModelChangeListeners() {
        this.appEvents.on(AppEvents.WindowResized, () => { this.resize(true); });
        this.appEvents.on(AppEvents.TotalSearchResults, (total) => { this.searchResultsTotalChange(total); });
        this.listenTo(this.model.DocumentHolders, "add", this.documentHolderAdded);
        this.listenTo(this.model.DocumentHolders, "remove", this.documentHolderRemoved);
        this.listenTo(this.model, "change:Focus", this.focusChanged);
        this.listenTo(this.model, "change:MultipleMode", this.changeMode);
        this.listenTo(this.model, "change:PrecedentMapMode", this.precedentMapModeChanged);
        this.listenTo(this.model, "change:DesktopLeftPossible", this.toggleLeftDesktop);
        this.listenTo(this.model, "change:DesktopRightPossible", this.toggleRightDesktop);
        this.listenTo(this.model, "change:SingleModePossible", this.toggleSingleMode);
        this.listenTo(this.model, "change:MultipleModePossible", this.toggleMultipleMode);
    }

    public onShow() {
        this.resize(false);
    }

    public enterSingleMode(): void {
        const documentHolders = this.model.DocumentHolders;
        const target = this.model.Focus;
        const others = documentHolders.without(target);
        $.each(others, (index: number, h: DocumentHolderModel) => {
            h.hide();
        });
        documentHolders.each((h: DocumentHolderModel) => {
            h.setSlot(1, TRANSITION.INSTANT);
            h.Compact = false;
        });

        target.setSlot(1, TRANSITION.INSTANT);
        this.model.updateButtons();
    }

    public documentHolderCollectionReset(collection: DocumentHolderCollection): void {
        const regions = this.masterView.getRegions();
        _.each(regions, (region) => {
            const parent = $(region.el).parent();
            // if they're under the document container, they're probably a document region
            if (parent.attr("id") === "desktop-container") {
                let name = region.el;
                name = name.substring(1);
                this.masterView.removeRegion(name);
            }
        });

        // re-add items if there are any new ones
        if (collection.length > 0) {
            collection.each((holder: DocumentHolderModel) => {
                this.documentHolderAdded(holder, collection);
            });
        }
    }

    public precedentMapModeChanged(dtop, model: CaseDocumentModel) {
        if (model) {
            this.enterPrecedentMap(model);
        } else {
            this.exitPrecedentMap(model);
        }
    }

    public enterMultipleMode() {
        const documentHolders = this.model.DocumentHolders;
        const target = this.model.Focus;
        const targetSlotPosition = this.model.TargetSlotPosition;
        const idx = documentHolders.indexOf(target);

        this.compactAllDocumentHolders();

        this.setSlotsForHoldersBefore(documentHolders, idx, targetSlotPosition, TRANSITION.INSTANT);
        this.setSlotsForHoldersAfter(documentHolders, idx, targetSlotPosition, TRANSITION.INSTANT);

        $.each(documentHolders.without(target), (index, h) => {
            h.show();
        });
        if (target) {
            target.setSlot(targetSlotPosition, TRANSITION.INSTANT); }
        this.model.updateButtons();
    }

    private setupSorter() {
        this.$(".open-documents-list").sortable({
            axis: "y",
            update: (event, ui) => {
                this.changeSortOrder($(ui.item).parent());
            }
        });
    }

    private changeSortOrder(sortElement) {
         const elems = $(sortElement).find("li");
         $.each(elems, (index, elem) => {
            const model = this.model.getDocumentHolderWithDocument($(elem).attr("data-id"));
            model.Slot = index + 1;
            model.trigger("SlotChange");
         });
    }

    private compactAllDocumentHolders(): void {
        const documentHolders = this.model.DocumentHolders;
        documentHolders.each((holder: DocumentHolderModel) => {
            holder.Compact = true;
        });
    }

    private setSlotsForHoldersBefore(documentHolders: DocumentHolderCollection, targetIndex: number, targetSlotPosition: number, transition: TRANSITION) {
        $.each(documentHolders.first(targetIndex), (index, h) => {
            const pos = documentHolders.indexOf(h);
            const newSlot = targetSlotPosition - (targetIndex - pos); // Note index variable is the loop position, not position in documentHolderCollection
            h.setSlot(newSlot, transition);
        });
    }

    private setSlotsForHoldersAfter(documentHolders: DocumentHolderCollection, targetIndex: number, targetSlotPosition: number, transition: TRANSITION) {
        $.each(documentHolders.last(documentHolders.length - (targetIndex + 1)), (index, h) => {
            const newSlot = targetSlotPosition + (index + 1); // Note index variable is the loop position, not position in documentHolderCollection
            h.setSlot(newSlot, transition);
        });
    }

    private exitPrecedentMap(model: CaseDocumentModel) {
        const documentHolders = this.model.DocumentHolders;
        $("#desktop-region").show();
        const targetHolder = documentHolders.filter((holder: DocumentHolderModel) => {
            return holder.Documents.get(model.Id) !== undefined;
        })[0];
        targetHolder.set("PrecedentMap", "exit");
    }

    private enterPrecedentMap(model: CaseDocumentModel) {
        const documentHolders = this.model.DocumentHolders;
        const targetHolder = documentHolders.filter((holder: DocumentHolderModel) => {
            return holder.Documents.get(model.Id) !== undefined;
        })[0];
        targetHolder.set("PrecedentMap", "enter");
        documentHolders.without(targetHolder).forEach((holder) => {
            holder.set("PrecedentMap", "avoid");
        });
    }
    private closeDocument(e: JQueryEventObject): void {
        e.stopPropagation();
        const docId = $(e.currentTarget).data("id");

        $("button#open-documents-button").attr("data-keep-dropdown-open", "true"); // We don't want the dropdown to close if route changes

        const documentHolderModel = this.model.DocumentHolders.findWhere((x: DocumentHolderModel) => x.getDocumentById(docId));

        if (documentHolderModel) {
            this.appEvents.trigger(AppEvents.DocumentHolderClosed, documentHolderModel);
            this.model.updateButtons();
        }
    }

    private resultsReturnClick(e: JQueryEventObject): void {
        if ($(e.currentTarget).hasClass("disabled")) { return; }
        Backbone.history.navigate("search", true);
    }

    private leftDesktopClick(e: JQueryEventObject): void {
        if ($(e.currentTarget).hasClass("disabled")) { return; }
        this.model.moveLeft();
    }

    private rightDesktopClick(e: JQueryEventObject): void {
        if ($(e.currentTarget).hasClass("disabled")) { return; }
        this.model.moveRight();
    }

    private desktopModeButtonClick(e: JQueryEventObject): void {
        if ($(e.currentTarget).hasClass("disabled")) { return; }
        const mode = $(e.currentTarget).attr("data-mode");
        const bool = (mode === "true");
        $(e.currentTarget).blur();
        this.model.MultipleMode = bool;
        this.rotateGraph($(window).width(), this.model.MultipleMode);
        this.appEvents.trigger(AppEvents.AppInsightsDesktopDocumentView, {MultipleMode: this.model.MultipleMode});
        this.model.navigateToDocument(this.model.Focus.TargetModel);
    }

    private rotateGraph(width: number, multiViewMode: boolean) {
        if (width === window.screen.availWidth && !multiViewMode) {
            this.$(dom.CATEGORY_TREND).removeClass("rotate-graph");
        } else {
            this.$(dom.CATEGORY_TREND).addClass("rotate-graph");
        }
    }

    private desktopStackButtonClick(e: JQueryEventObject): void {
        this.model.DocumentHolders.each((holder, index) => {
            holder.StackPosition = index;
        });
    }

    private openDocumentsButtonClick(e: JQueryEventObject): void {
        this.showOpenDocuments(); // This functionality is moved into a separate method so it can be called from the code without a JQueryEventObject
    }
    private showOpenDocuments(): void {
        const docs = this.model.getDocumentList();
        let html = "";
        let closeButtonhtml = "";
        docs.forEach((doc: any) => {
            let displayTitle = doc.Title;
            const maxCharacters = 25;
            if (displayTitle.length > maxCharacters) {
                displayTitle = displayTitle.substring(0, maxCharacters) + "...";
            }
            closeButtonhtml = "<i class=\"fa fa-remove document-closer in-dropdown\" data-id=\"" + doc.Id + "\" aria-hidden=\"true\" ></i>";
            html += "<li class=\"open-document-link\" data-id=\"" + doc.Id + "\" role=\"menuitem\"><span class=\"case-container\">"
                 + "<a class=\"case-link js-order\" title=\"" + doc.Title + "\" href=\"" + doc.Url + "\">" + displayTitle + "</a>" + closeButtonhtml + "</span></li>";
        });
        if (docs.length !== 0) {
            $(".open-documents-list").html(html);
        }
    }

    private toggleLeftDesktop(model, possible: boolean): void {
        _.defer(() => {
            this.toggleDisabledStateOfControl(this.$(dom.LEFT_BUTTON), !possible);
        });
    }

    private toggleRightDesktop(model, possible: boolean): void {
        _.defer(() => {
            this.toggleDisabledStateOfControl(this.$(dom.RIGHT_BUTTON), !possible);
        });
    }

    private toggleMultipleMode(model, possible: boolean): void {
        _.defer(() => {
            this.toggleDisabledStateOfControl(this.$(dom.OPEN_DOCUMENTS_BUTTON), !possible);

            const desktopModePossible = !possible && this.openCiteLineCount === 0;
            this.toggleDisabledStateOfControl(this.$(dom.DESKTOP_MODE_BUTTON), desktopModePossible);
        });
    }

    private toggleDisabledStateOfControl(e: JQuery, disabled: boolean): void {
        e.toggleClass("disabled", disabled).attr("aria-disabled", disabled.toString());
    }

    private toggleSingleMode(model, possible: boolean): void {
            this.$(dom.DESKTOP_MODE_BUTTON).fadeToggle(possible);
    }

    private toggleDesktopModeButtons(multipleMode) {
        const $btn = this.$(dom.DESKTOP_MODE_BUTTON);
        $btn.find(".single-view-icon").toggle(multipleMode);
        $btn.find(".multi-view-icon").toggle(!multipleMode);
        $btn.find(".mode-label").text(multipleMode ? "Single View" : "Multiple View");
        $btn.attr("data-mode", (!multipleMode).toString());
    }

    private openDocumentLinkClick(e: JQueryEventObject): boolean {
        e.preventDefault();
        const url = $(e.currentTarget).find("a").attr("href");
        if (url) {
            Backbone.history.navigate(url, { trigger: true });
        }
        return true;
    }

    private searchResultsTotalChange(total) {
        this.$(dom.RESULTS_RETURN_BUTTON).toggleClass("disabled", !(total > 0));
    }

    /** Reorganises the desktop view
     * @param {number} width This may do something later
     */
    private resize(redistribute: boolean): void {
        if ((this.sizeManager.DesktopSlots < 2)) {
            this.model.MultipleMode = true;
        }
        if (redistribute) { this.distributeDocumentHolders(); }
        this.model.updateButtons();
    }

    private focusChanged(): void {

        const mh: MobileHelper = Container.instance.get(MobileHelper);
        if (mh.IsMobileMode) { //
            $(".mobile-document-list").hide();
            const id = this.model.Focus.InitialStageId;
            $(".document-holder").removeClass("active");
            $(`#${id}.document-holder`).addClass("active");
        }
        this.distributeDocumentHolders();
    }

    private documentHolderRemoved(deletedModel: DocumentHolderModel, collection: DocumentHolderCollection, options): void {
        _.delay(() => {
            const id = deletedModel.InitialStageId;
            const region = this.masterView.getRegion(id);
            region.reset();
            this.masterView.removeRegion(id);
            if (collection.length === 0) {
                this.$(".btn-group").removeClass("open"); // prevents bug where menu would remain open with faulty data if all docs were closed, then a new one was opened
            }
        }, 500);
        let index = options.index;
        if (index === collection.length && collection.length !== 0) { // special case for end of list - if we close last element we want others to shift over
            index = index - 1;
        }

        this.showOpenDocuments();
        this.model.updateButtons();
    }
    private documentHolderAdded(model: DocumentHolderModel, collection: DocumentHolderCollection): void {
        const id = model.InitialStageId;
        model.Compact = this.model.MultipleMode;
        $(`<div class="document-holder" id='${id}'></div>`).appendTo(dom.DESKTOP_CONTAINER);
        const desktopRegion = this.masterView.addRegion(id, `#${id}`);
        const documentHolder = Container.instance.get(DocumentHolderView);
        documentHolder.model = model;
        desktopRegion.show(documentHolder);
        this.model.Focus = model;
        this.focusChanged();
        this.model.updateButtons();

    }

    private distributeDocumentHolders() {
        const documentHolders = this.model.DocumentHolders;
        // this occasionally gets triggered when there aren't any documents
        if (documentHolders.length === 0) {
            return;
        }
        const target = this.model.Focus;
        if (target.TargetModel) {
            target.TargetModel.trigger(DocumentEvents.GainedFocus);
        }
        const targetSlotPosition = this.model.TargetSlotPosition;
        const targetIndex = documentHolders.indexOf(target);
        target.setSlot(targetSlotPosition, TRANSITION.ANIMATED);
        target.show();
        const others = documentHolders.without(target);
        if (this.model.MultipleMode) {
            this.setSlotsForHoldersBefore(documentHolders, targetIndex, targetSlotPosition, TRANSITION.ANIMATED);
            this.setSlotsForHoldersAfter(documentHolders, targetIndex, targetSlotPosition, TRANSITION.ANIMATED);
        } else {
            $.each(others, (index: number, h: DocumentHolderModel) => {
                h.hide();
            });
        }

        this.model.updateButtons();
    }

    /** Compacts or expands the DocumentHolders in the view depending on whether Multiple mode is on or not */
    private changeMode(model, property): void {
        this.toggleDesktopModeButtons(property);
        if (property) {
            this.enterMultipleMode();
        } else {
            this.enterSingleMode();
        }
    }

     private swipeLeft() {
        if (this.model.DesktopRightPossible) {
            this.model.moveRight();
        }
    }

    private swipeRight() {
        if (this.model.DesktopLeftPossible) {
            this.model.moveLeft();
        } else {
            Backbone.history.navigate("search", { trigger: true });
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/desktop.ts